import { useRef, useState } from "react";
import { Link } from "react-router";
import {
  CONTRACT_BILLING_LABELS,
  dueText,
  formatEuro,
  formatIntervalOf,
  INTAKE_STATUS_LABELS,
  OBLIGATION_BASIS_APP_LABELS,
  OBLIGATION_KIND_LABELS,
  OBLIGATION_KIND_TEXT,
  OBLIGATION_STATUS_LABELS,
  obligationStatusLabel,
  todayIso,
  type AssetObligationDto,
  type AssetObligationsResponse,
  type AssetDetail,
  type MetrelRecordData,
  type ObligationKind,
  type ObligationRecordDto,
  type ObligationStatus,
} from "@klantpunt/shared";
import { IconPencil, IconPlus, IconTrash } from "../../app/icons";
import { api, ApiError } from "../../lib/api";
import { can, hasModule, useMe } from "../../lib/auth";
import { useToast } from "../../lib/toast";
import { ExecutionPanel } from "./ExecutionPanel";
import { RecordDialog, uploadCertificate } from "./RecordDialog";
import { useAssetObligations, useObligationsCache, usePendingRecords } from "./useObligations";

const errMsg = (e: unknown) => (e instanceof ApiError ? e.message : "Er ging iets mis");
export const fmtDay = (iso: string | null) =>
  iso
    ? new Date(`${iso}T12:00:00`).toLocaleDateString("nl-NL", { day: "numeric", month: "short", year: "numeric" })
    : "—";
const every = (o: { intervalMonths: number | null; intervalWeeks: number | null }) =>
  o.intervalMonths || o.intervalWeeks ? `elke ${formatIntervalOf(o.intervalMonths, o.intervalWeeks)}` : "eenmalig";

/** Status; met `label` bijv. "Afgekeurd" of "Probleem" in plaats van "Verlopen" (zie `obligationStatusLabel`). */
export function StatusChip({ status, small, label }: { status: ObligationStatus; small?: boolean; label?: string }) {
  return (
    <span className={`chip obst ob-${status}${small ? " small" : ""}`}>
      <i aria-hidden="true" />
      {label ?? OBLIGATION_STATUS_LABELS[status]}
    </span>
  );
}

/**
 * Uitvoeringen bij een asset (docs/ASSETS-MJOP.md): status per uitvoering, toevoegen en wijzigen
 * met hetzelfde formulier als in de MJOP-planning, vastleggen per soort (ook offline) en historie. Bovenaan het paneel, zodat
 * een gescande QR-code eerst de status en de volgende keer laat zien.
 */
export function ObligationSection({
  asset,
  buildingId,
  buildingLabel,
}: {
  /** null = onderhoud en keuringen bij het gebouw zelf. */
  asset: AssetDetail | null;
  buildingId: string;
  /** Kop van het uitvoeringspaneel bij het gebouw, bijv. "W455 · Weena 455". */
  buildingLabel?: string;
}) {
  const { data: me } = useMe();
  const toast = useToast();
  const assetId = asset?.id ?? null;
  const q = useAssetObligations(buildingId, assetId);
  const set = useObligationsCache(buildingId, assetId);
  const canManage = can(me, "asset.manage");
  // null = dicht, "new" = nieuwe uitvoering, anders het id.
  const [form, setForm] = useState<string | null>(null);
  const [recordFor, setRecordFor] = useState<AssetObligationDto | null>(null);
  const [busy, setBusy] = useState(false);
  const pending = usePendingRecords(q.data?.obligations.map((o) => o.id) ?? []);
  const today = todayIso();

  async function run(fn: () => Promise<AssetObligationsResponse>, ok: string) {
    setBusy(true);
    try {
      set(await fn());
      toast(ok);
    } catch (e) {
      toast(errMsg(e));
    } finally {
      setBusy(false);
    }
  }
  if (q.isPending) return <section className="sec muted small">Onderhoud en keuringen laden…</section>;
  if (!q.data) return null;
  const { obligations } = q.data;
  if (!obligations.length && !canManage) return null;

  // De samenvatting (status, volgende keuring) staat bovenaan het assetpaneel; hier alleen de kaarten.
  return (
    <section className="sec obsec" aria-label="Onderhoud en keuringen">
      <p className="label">Onderhoud en keuringen</p>

      {obligations.map((o) => (
        <ObligationCard
          key={o.id}
          o={o}
          equipment={asset?.assetKind === "equipment"}
          showCost={canManage}
          today={today}
          pending={pending.filter((p) => p.obligationId === o.id).map((p) => p.body.performedOn)}
          canManage={canManage}
          busy={busy}
          onRecord={() => setRecordFor(o)}
          onCertificate={(recordId, f) =>
            void run(async () => {
              const fileId = await uploadCertificate(f);
              return api<AssetObligationsResponse>(`/obligation-records/${recordId}`, {
                method: "PATCH",
                body: { certificateFileId: fileId },
              });
            }, `${OBLIGATION_KIND_TEXT[o.kind].document} toegevoegd`)
          }
          onEdit={() => setForm(o.id)}
          onDelete={() => {
            if (
              confirm(`"${o.name}" verwijderen bij ${asset ? "deze asset" : "dit gebouw"}? De historie gaat ook weg.`)
            )
              void run(
                () => api<AssetObligationsResponse>(`/asset-obligations/${o.id}`, { method: "DELETE" }),
                "Uitvoering verwijderd",
              );
          }}
        />
      ))}
      {!obligations.length && (
        <p className="muted small">Nog geen onderhoud of keuringen bij {asset ? "deze asset" : "dit gebouw"}.</p>
      )}
      {canManage && (
        <div>
          <button type="button" className="btn small" onClick={() => setForm("new")}>
            <IconPlus size={16} aria-hidden="true" />
            Uitvoering toevoegen
          </button>
        </div>
      )}

      {form && (
        <ExecutionPanel
          assetId={assetId}
          buildingId={buildingId}
          assetLabel={asset ? `${asset.code} · ${asset.name}` : (buildingLabel ?? "Gebouw")}
          obligationId={form === "new" ? null : form}
          canEdit={canManage}
          showCost={hasModule(me, "mjop")}
          onClose={() => setForm(null)}
        />
      )}
      {recordFor && (
        <RecordDialog
          asset={asset}
          buildingId={buildingId}
          obligation={recordFor}
          onClose={() => setRecordFor(null)}
          onSaved={(r) => {
            if (r) set(r);
            setRecordFor(null);
          }}
        />
      )}
    </section>
  );
}

function RecordLine({ r, kind }: { r: ObligationRecordDto; kind: ObligationKind }) {
  return (
    <li className="small">
      <strong>{fmtDay(r.performedOn)}</strong> · {OBLIGATION_KIND_TEXT[kind].results[r.result]}
      {r.performedByCompany && ` · ${r.performedByCompany}`}
      {r.certification && ` (${r.certification})`}
      {r.certificateFileId && (
        <>
          {" · "}
          <a href={`/api/files/${r.certificateFileId}/url`} target="_blank" rel="noopener">
            {OBLIGATION_KIND_TEXT[kind].document.toLowerCase()}
          </a>
        </>
      )}
      {r.findings.length > 0 && (
        <span className="muted">
          {" · "}
          {r.findings.length === 1 ? "bevinding" : "bevindingen"} {r.findings.map((f) => f.code).join(", ")}
        </span>
      )}
      {r.intake && (r.intake.status === "new" || !r.findings.length) && (
        <span className="muted">
          {" · "}
          {r.intake.status === "new"
            ? "in de Inbox, te beoordelen"
            : `melding ${INTAKE_STATUS_LABELS[r.intake.status].toLowerCase()}`}{" "}
          (<Link to={`/inbox?id=${r.intake.id}`}>{r.intake.code}</Link>)
        </span>
      )}
      {r.notes && <span className="muted block prewrap">{r.notes}</span>}
      {r.measurements && <Measurements m={r.measurements} />}
    </li>
  );
}

/** Metingen en checklist van een keuring uit de meter (Metrel-import). */
function Measurements({ m }: { m: MetrelRecordData }) {
  const checks = m.checklist.length;
  const checksOk = m.checklist.filter((c) => c.passed).length;
  return (
    <details className="obmeas">
      <summary className="muted">
        {m.procedure ?? "Metingen"} · {m.measurements.length} {m.measurements.length === 1 ? "meting" : "metingen"}
        {checks ? ` · checklist ${checksOk}/${checks} goed` : ""}
      </summary>
      {m.measurements.length > 0 && (
        <table className="obmeas-table">
          <thead>
            <tr>
              <th scope="col">Meting</th>
              <th scope="col">Waarde</th>
              <th scope="col">Grens</th>
              <th scope="col">Oordeel</th>
            </tr>
          </thead>
          <tbody>
            {m.measurements.map((x, i) => (
              <tr key={i}>
                <td>{x.name}</td>
                <td className="num">{x.value ?? "—"}</td>
                <td className="num">{x.limit ?? "—"}</td>
                <td className={x.passed === false ? "obdue over" : undefined}>
                  {x.passed == null ? "—" : x.passed ? "Goed" : "Fout"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {checks > 0 && (
        <ul className="obmeas-checks">
          {m.checklist.map((c, i) => (
            <li key={i}>
              {c.item}: {c.passed ? "goed" : "niet beoordeeld"}
            </li>
          ))}
        </ul>
      )}
    </details>
  );
}

function ObligationCard({
  o,
  equipment,
  today,
  pending,
  canManage,
  showCost,
  busy,
  onRecord,
  onCertificate,
  onEdit,
  onDelete,
}: {
  o: AssetObligationDto & { records: ObligationRecordDto[] };
  /** Arbeidsmiddel: afgekeurd betekent gelabeld en buiten gebruik gesteld. */
  equipment: boolean;
  /** Kosten alleen voor wie assets beheert (niet voor klanten of uitvoerders). */
  showCost: boolean;
  today: string;
  pending: string[];
  canManage: boolean;
  busy: boolean;
  onRecord: () => void;
  onCertificate: (recordId: string, f: File) => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const [history, setHistory] = useState(false);
  const certInput = useRef<HTMLInputElement>(null);
  const kt = OBLIGATION_KIND_TEXT[o.kind];
  const needsCert = canManage && o.lastRecord && !o.lastRecord.certificateFileId;
  return (
    <article className={`obcard ob-${o.status}`} aria-label={o.name}>
      <div className="row">
        <strong className="grow">{o.name}</strong>
        <StatusChip status={o.status} small label={obligationStatusLabel(o.status, o.kind, o.lastRecord?.result)} />
      </div>
      {equipment && o.lastRecord?.result === "afgekeurd" && (
        <p className="small obdue over" role="status">
          Afgekeurd, buiten gebruik
        </p>
      )}
      {o.notApplicable ? (
        <p className="small muted">Niet van toepassing: {o.overrideReason}</p>
      ) : (
        <p className="small">
          {o.nextDueOn ? (
            <>
              Volgende {kt.noun.toLowerCase()} <strong>{fmtDay(o.nextDueOn)}</strong>{" "}
              <span className={o.status === "overdue" ? "obdue over" : "obdue"}>({dueText(o.nextDueOn, today)})</span>
            </>
          ) : (
            <span className="muted">Datum laatste {kt.noun.toLowerCase()} onbekend</span>
          )}
        </p>
      )}
      <p className="small muted">
        {OBLIGATION_KIND_LABELS[o.kind]} · {every(o)} · {OBLIGATION_BASIS_APP_LABELS[o.basis]}
        {showCost && o.costCents != null && ` · ${formatEuro(o.costCents)} per keer`}
        {o.performer && ` · ${o.performer}`}
      </p>
      {o.contract && (
        <p className="small">
          Contract <Link to={`/contracten/${o.contract.contractId}?tab=dekking`}>{o.contract.contractName}</Link>
          <span className="muted">
            {" "}
            · {CONTRACT_BILLING_LABELS[o.contract.billing]}
            {o.contract.shareCents != null && ` · indicatie ${formatEuro(o.contract.shareCents)} per keer`}
            {o.contract.state === "beeindigd" && " · afgelopen"}
          </span>
        </p>
      )}
      {o.sourceUrl && (
        <a className="small" href={o.sourceUrl} target="_blank" rel="noopener noreferrer">
          Bron: {o.sourceTitle ?? "link"}
        </a>
      )}
      {pending.length > 0 && (
        <p className="small obpending" role="status">
          {kt.noun} van {pending.map(fmtDay).join(", ")} wacht op verbinding
        </p>
      )}
      {o.lastRecord && (
        <ul className="obhist">
          <RecordLine r={o.lastRecord} kind={o.kind} />
        </ul>
      )}
      <div className="btnrow">
        {canManage && !o.notApplicable && (
          <button type="button" className="btn small primary" onClick={onRecord}>
            {kt.record}
          </button>
        )}
        {needsCert && (
          <>
            <button type="button" className="btn small" disabled={busy} onClick={() => certInput.current?.click()}>
              {kt.document} toevoegen
            </button>
            <input
              ref={certInput}
              type="file"
              hidden
              accept=".pdf,.png,.jpg,.jpeg,.webp"
              aria-label={`${kt.document} bij ${o.name}`}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f && o.lastRecord) onCertificate(o.lastRecord.id, f);
                e.target.value = "";
              }}
            />
          </>
        )}
        {o.records.length > 1 && (
          <button type="button" className="btn small" aria-expanded={history} onClick={() => setHistory(!history)}>
            Historie ({o.records.length})
          </button>
        )}
        <span className="grow" />
        {canManage && (
          <>
            <button type="button" className="btn small ghost ico" aria-label={`${o.name} bewerken`} onClick={onEdit}>
              <IconPencil size={16} aria-hidden="true" />
            </button>
            <button
              type="button"
              className="btn small ghost ico"
              aria-label={`${o.name} verwijderen`}
              disabled={busy}
              onClick={onDelete}
            >
              <IconTrash size={16} aria-hidden="true" />
            </button>
          </>
        )}
      </div>
      {history && (
        <ul className="obhist">
          {o.records.slice(1).map((r) => (
            <RecordLine key={r.id} r={r} kind={o.kind} />
          ))}
        </ul>
      )}
    </article>
  );
}
